import { defineMutators } from "@rocicorp/zero";
import { associateBranchChatWithConnectedApp } from "./associateBranchChatWithConnectedApp";
import { associatePersonalChatWithConnectedApp } from "./associatePersonalChatWithConnectedApp";
import { associateStepWithConnectedApp } from "./associateStepWithConnectedApp";
import { closePersonalChat } from "./closePersonalChat";
import { completeProfile } from "./completeProfile";
import { configureGitSync } from "./configureGitSync";
import { createApiKey } from "./createApiKey";
import { createAuditLogDestination } from "./createAuditLogDestination";
import { createBranch } from "./createBranch";
import { createConnectedApp } from "./createConnectedApp";
import { createEgressRoute } from "./createEgressRoute";
import { createFeatureFlag } from "./createFeatureFlag";
import { createEgressRule } from "./createEgressRule";
import { createNetwork } from "./createNetwork";
import { createPersonalChat } from "./createPersonalChat";
import { createRole } from "./createRole";
import { createServiceAccount } from "./createServiceAccount";
import { createServiceAccountFederation } from "./createServiceAccountFederation";
import { createServiceAccountSession } from "./createServiceAccountSession";
import { createUserApiKey } from "./createUserApiKey";
import { createSpace } from "./createSpace";
import { createSkill } from "./createSkill";
import { createUserGroup } from "./createUserGroup";
import { createWorkflow } from "./createWorkflow";
import { deactivateUser } from "./deactivateUser";
import { deleteAIConfig } from "./deleteAIConfig";
import { deleteApiKey } from "./deleteApiKey";
import { deleteAuditLogDestination } from "./deleteAuditLogDestination";
import { deleteBranch } from "./deleteBranch";
import { deleteConnectedApp } from "./deleteConnectedApp";
import { deleteEgressRoute } from "./deleteEgressRoute";
import { deleteFeatureFlag } from "./deleteFeatureFlag";
import { deleteGitSkillSource } from "./deleteGitSkillSource";
import { deleteEgressRule } from "./deleteEgressRule";
import { deleteNetwork } from "./deleteNetwork";
import { deleteRole } from "./deleteRole";
import { deleteSkill } from "./deleteSkill";
import { deleteSkillFile } from "./deleteSkillFile";
import { deleteServiceAccount } from "./deleteServiceAccount";
import { deleteServiceAccountFederation } from "./deleteServiceAccountFederation";
import { deleteOidcProvider } from "./deleteOidcProvider";
import { deleteSpace } from "./deleteSpace";
import { deleteSSOProvider } from "./deleteSSOProvider";
import { deleteUsageBudget } from "./deleteUsageBudget";
import { deleteUsagePrice } from "./deleteUsagePrice";
import { deleteUserGroup } from "./deleteUserGroup";
import { deleteWorkflow } from "./deleteWorkflow";
import { deriveWorkflowFromChat } from "./deriveWorkflowFromChat";
import { disableFeatureFlag } from "./disableFeatureFlag";
import { disconnectGitSync } from "./disconnectGitSync";
import { dissociatePersonalChatFromConnectedApp } from "./dissociatePersonalChatFromConnectedApp";
import { dissociateStepFromConnectedApp } from "./dissociateStepFromConnectedApp";
import { enableFeatureFlag } from "./enableFeatureFlag";
import { forkPersonalChat } from "./forkPersonalChat";
import { generateScimToken } from "./generateScimToken";
import { importWorkflow } from "./importWorkflow";
import { inspectGitSkillSource } from "./inspectGitSkillSource";
import { installGitSkills } from "./installGitSkills";
import { interruptChat } from "./interruptChat";
import { interruptPersonalChat } from "./interruptPersonalChat";
import { inviteOrReactivateUser } from "./inviteOrReactivateUser";
import { joinSpace } from "./joinSpace";
import { markMonitoringTabViewed } from "./markMonitoringTabViewed";
import { markRouteViewed } from "./markRouteViewed";
import { markWorkflowViewed } from "./markWorkflowViewed";
import { mergeMainIntoBranch } from "./mergeMainIntoBranch";
import { moveWorkflow } from "./moveWorkflow";
import { type AppMutation, traceMutators } from "./mutator";
import { permanentlyDeleteWorkflow } from "./permanentlyDeleteWorkflow";
import { pauseWorkflow } from "./pauseWorkflow";
import { patchScopeGrants } from "./patchScopeGrants";
import { pinStepTest } from "./pinStepTest";
import { renameStepTest } from "./renameStepTest";
import { unpinStepTest } from "./unpinStepTest";
import { providePersonalChatToolResult } from "./providePersonalChatToolResult";
import { provideSpaceChatToolResult } from "./provideSpaceChatToolResult";
import { publishConnectedApp } from "./publishConnectedApp";
import { publishSkill } from "./publishSkill";
import { pushBranchToLive } from "./pushBranchToLive";
import { reactivateUser } from "./reactivateUser";
import { recordDemoRequest } from "./recordDemoRequest";
import { recordModelExperimentEvent } from "./recordModelExperimentEvent";
import { recordMonitoringInteraction } from "./recordMonitoringInteraction";
import { recordPostSignupFormView } from "./recordPostSignupFormView";
import { recoverWorkflow } from "./recoverWorkflow";
import { resyncSkill } from "./resyncSkill";
import { refreshConnectorList } from "./refreshConnectorList";
import { restoreWorkflowOnBranch } from "./restoreWorkflowOnBranch";
import { reopenPersonalChat } from "./reopenPersonalChat";
import { reorderPersonalChat } from "./reorderPersonalChat";
import { reviewDuplicatePair } from "./reviewDuplicatePair";
import { refreshDuplicateDetection } from "./refreshDuplicateDetection";
import { reEmitRetainedExecution } from "./reEmitExecution";
import { authorizeOAuthAppForServiceAccount } from "./authorizeOAuthAppForServiceAccount";
import { deauthorizeOAuthAppForServiceAccount } from "./deauthorizeOAuthAppForServiceAccount";
import { deleteOAuthApp } from "./deleteOAuthApp";
import { registerOAuthApp } from "./registerOAuthApp";
import { revokeOAuthGrant } from "./revokeOAuthGrant";
import { updateOAuthApp } from "./updateOAuthApp";
import { registerOidcProvider } from "./registerOidcProvider";
import { registerSSOProvider } from "./registerSSOProvider";
import { removeCustomAvatar } from "./removeCustomAvatar";
import { recordGithubAppInstallation } from "./recordGithubAppInstallation";
import { renameBranch } from "./renameBranch";
import { renameWorkflow } from "./renameWorkflow";
import { resetOAuthConnection } from "./resetOAuthConnection";
import { dismissConnectionClaim } from "./dismissConnectionClaim";
import { resolveConnectionClaim } from "./resolveConnectionClaim";
import { approveGrantRequest } from "./approveGrantRequest";
import { cancelGrantRequest } from "./cancelGrantRequest";
import { dismissGrantRequest } from "./dismissGrantRequest";
import { requestScopeRoleGrants } from "./requestScopeRoleGrants";
import { restoreToCommit } from "./restoreToCommit";
import { revokeAllSessions } from "./revokeAllSessions";
import { revokeScimToken } from "./revokeScimToken";
import { runStep } from "./runStep";
import { runStepTests } from "./runStepTests";
import { saveToHistory } from "./saveToHistory";
import { sendMessage } from "./sendMessage";
import { sendPersonalChatMessage } from "./sendPersonalChatMessage";
import { setWorkflowRetention } from "./setWorkflowRetention";
import { setBranchModelPreference } from "./setBranchModelPreference";
import { setWorkflowTimeSaved } from "./setWorkflowTimeSaved";
import { setCustomAvatar } from "./setCustomAvatar";
import { setGroupGrants } from "./setGroupGrants";
import { setAIProviderDisabled } from "./setAIProviderDisabled";
import { setManagedAIProviderDisabled } from "./setManagedAIProviderDisabled";
import { setServiceAccountTenantRoles } from "./setServiceAccountTenantRoles";
import { setTenantAdmins } from "./setTenantAdmins";
import { setUserGroupTenantRoles } from "./setUserGroupTenantRoles";
import { setPersonalChatPinned } from "./setPersonalChatPinned";
import { setPublicRouteAuthDisabled } from "./setPublicRouteAuthDisabled";
import { setRouteAuthPolicy } from "./setRouteAuthPolicy";
import { setUserPreference } from "./setUserPreference";
import { setWorkflowAppBacking } from "./setWorkflowAppBacking";
import { switchConnectorVariant } from "./switchConnectorVariant";
import { syncGit } from "./syncGit";
import { testAuditLogDestination } from "./testAuditLogDestination";
import { testConnectedApp } from "./testConnectedApp";
import { tidyUpFreshConnectedApp } from "./tidyUpFreshConnectedApp";
import { toggleAutofix } from "./toggleAutofix";
import { toggleAutoImprove } from "./toggleAutoImprove";
import { toggleRouteFavorite } from "./toggleRouteFavorite";
import { toggleStepRoutePin } from "./toggleStepRoutePin";
import { toggleWorkflowFavorite } from "./toggleWorkflowFavorite";
import { touchPersonalChat } from "./touchPersonalChat";
import { touchPresence } from "./touchPresence";
import { unpauseWorkflow } from "./unpauseWorkflow";
import { updateAIConfig } from "./updateAIConfig";
import { updateAIBudget } from "./updateAIBudget";
import { updateAuditLogDestination } from "./updateAuditLogDestination";
import { updateConnectedApp } from "./updateConnectedApp";
import { updateEgressRoute } from "./updateEgressRoute";
import { updateEgressRule } from "./updateEgressRule";
import { updateOAuthConnection } from "./updateOAuthConnection";
import { updateOidcProvider } from "./updateOidcProvider";
import { updateServiceAccount } from "./updateServiceAccount";
import { updateSpace } from "./updateSpace";
import { updateSSOProvider } from "./updateSSOProvider";
import { updateStepColor } from "./updateStepColor";
import { updateStepRouteType } from "./updateStepRouteType";
import { updateStepTimeout } from "./updateStepTimeout";
import { updateSkill } from "./updateSkill";
import { upsertSkillFile } from "./upsertSkillFile";
import { upsertSkillFiles } from "./upsertSkillFiles";
import { updateUsageBudget } from "./updateUsageBudget";
import { updateUsagePrice } from "./updateUsagePrice";
import { updateUserGroup } from "./updateUserGroup";
import { updateWorkflowBadge } from "./updateWorkflowBadge";

const definitions = {
  associateBranchChatWithConnectedApp,
  associatePersonalChatWithConnectedApp,
  associateStepWithConnectedApp,
  closePersonalChat,
  completeProfile,
  configureGitSync,
  createApiKey,
  createAuditLogDestination,
  createConnectedApp,
  createEgressRoute,
  createEgressRule,
  createFeatureFlag,
  createNetwork,
  createPersonalChat,
  createRole,
  createSkill,
  createBranch,
  createServiceAccount,
  createServiceAccountFederation,
  createServiceAccountSession,
  createUserApiKey,
  deleteServiceAccountFederation,
  setServiceAccountTenantRoles,
  registerOidcProvider,
  updateOidcProvider,
  deleteOidcProvider,
  registerOAuthApp,
  updateOAuthApp,
  deleteOAuthApp,
  authorizeOAuthAppForServiceAccount,
  deauthorizeOAuthAppForServiceAccount,
  revokeOAuthGrant,
  createSpace,
  createUserGroup,
  createWorkflow,
  deactivateUser,
  deleteAIConfig,
  deleteAuditLogDestination,
  inviteOrReactivateUser,
  deleteBranch,
  deleteConnectedApp,
  deleteEgressRoute,
  deleteEgressRule,
  deleteFeatureFlag,
  deleteGitSkillSource,
  deleteNetwork,
  deleteRole,
  deleteUsageBudget,
  deleteUsagePrice,
  deleteUserGroup,
  disableFeatureFlag,
  disconnectGitSync,
  deleteSSOProvider,
  deleteServiceAccount,
  deleteSkill,
  deleteSkillFile,
  deleteSpace,
  deleteWorkflow,
  deriveWorkflowFromChat,
  dissociatePersonalChatFromConnectedApp,
  dissociateStepFromConnectedApp,
  enableFeatureFlag,
  forkPersonalChat,
  generateScimToken,
  importWorkflow,
  inspectGitSkillSource,
  installGitSkills,
  interruptChat,
  interruptPersonalChat,
  joinSpace,
  markMonitoringTabViewed,
  markRouteViewed,
  markWorkflowViewed,
  mergeMainIntoBranch,
  moveWorkflow,
  patchScopeGrants,
  pauseWorkflow,
  permanentlyDeleteWorkflow,
  pinStepTest,
  providePersonalChatToolResult,
  provideSpaceChatToolResult,
  publishConnectedApp,
  publishSkill,
  pushBranchToLive,
  reactivateUser,
  recordDemoRequest,
  recordModelExperimentEvent,
  recordMonitoringInteraction,
  recordPostSignupFormView,
  recoverWorkflow,
  renameStepTest,
  resyncSkill,
  refreshConnectorList,
  restoreWorkflowOnBranch,
  reopenPersonalChat,
  reorderPersonalChat,
  reviewDuplicatePair,
  refreshDuplicateDetection,
  registerSSOProvider,
  reEmitRetainedExecution,
  removeCustomAvatar,
  renameBranch,
  renameWorkflow,
  resetOAuthConnection,
  resolveConnectionClaim,
  dismissConnectionClaim,
  approveGrantRequest,
  cancelGrantRequest,
  dismissGrantRequest,
  requestScopeRoleGrants,
  recordGithubAppInstallation,
  restoreToCommit,
  revokeAllSessions,
  revokeScimToken,
  deleteApiKey,
  runStep,
  runStepTests,
  saveToHistory,
  sendMessage,
  sendPersonalChatMessage,
  setCustomAvatar,
  setGroupGrants,
  setAIProviderDisabled,
  setManagedAIProviderDisabled,
  setTenantAdmins,
  setUserGroupTenantRoles,
  setPersonalChatPinned,
  setPublicRouteAuthDisabled,
  setRouteAuthPolicy,
  setUserPreference,
  setBranchModelPreference,
  setWorkflowRetention,
  setWorkflowTimeSaved,
  setWorkflowAppBacking,
  switchConnectorVariant,
  syncGit,
  testAuditLogDestination,
  testConnectedApp,
  tidyUpFreshConnectedApp,
  toggleAutofix,
  toggleAutoImprove,
  toggleRouteFavorite,
  toggleStepRoutePin,
  toggleWorkflowFavorite,
  touchPersonalChat,
  touchPresence,
  unpauseWorkflow,
  unpinStepTest,
  updateAIConfig,
  updateAIBudget,
  updateAuditLogDestination,
  updateStepColor,
  updateStepRouteType,
  updateStepTimeout,
  updateConnectedApp,
  updateEgressRoute,
  updateEgressRule,
  updateServiceAccount,
  updateSSOProvider,
  updateSkill,
  upsertSkillFile,
  upsertSkillFiles,
  updateOAuthConnection,
  updateSpace,
  updateUsageBudget,
  updateUsagePrice,
  updateUserGroup,
  updateWorkflowBadge,
} satisfies Record<string, AppMutation>;

export { makeBranchParams } from "./utils";
export type { AppMutation } from "./mutator";
export const sharedMutators = defineMutators(traceMutators(definitions));
// The registry above erases the `resolve` / `permissions` props from its
// types; server mutators reference the shared declarations through this.
export const sharedMutatorDefs = definitions;
export type MutatorName = keyof typeof definitions;
